import { LitElement, TemplateResult, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import urlJoin from 'url-join';
import {
  PRIVILEGED_REMOTE_ACCESS_SLUG,
  ProductMenuItem,
  REMOTE_SUPPORT_SLUG,
  SECRETS_SLUG,
  SiteData,
} from '../../../types';
import '../../screen-reader-only/screen-reader-only';
import styles from './menu.styles.scss';

import { ContextConsumer } from '@lit/context';
import { localized, msg } from '@lit/localize';
import { choose } from 'lit/directives/choose.js';
import { PRODUCT_MENU_ITEMS, REPORTING_PRODUCTS } from '../../../constants';
import { createSelectedSiteActionEvent } from '../../../context';
import { resolveCurrentProduct } from '../../../selectors/menu-selectors';
import { sitesDataContext } from '../../../context/sites-data';
import { flagContext } from '../../../feature-flags';
import { t } from '../../../services/runtime-i18n';
import '../../product-icon/product-icon';
import '../../tag/tag';
import '../cross-product-sub-menu/cross-product-sub-menu';
import '../defaults';
import '../menu-item-icon/menu-item-icon';
import '../more-products-sub-menu/more-products-sub-menu';
import '../product-menu/product-menu';
import '../product-sub-menu/product-sub-menu';

export const MENU_TAG = 'platform-menu' as const;

const DIALOG_ID = 'platform-dialog' as const;

const MORE_PRODUCTS_SLUG = 'more-products';

/**
 * Common menu used for navigation.
 */
@customElement(MENU_TAG)
@localized()
export class SpogMenu extends LitElement {
  static override styles = [styles];

  @property({ type: Array })
  productMenuItems!: ProductMenuItem[];

  @property({ attribute: false })
  currentSite?: SiteData;

  @query(`#${DIALOG_ID}`)
  private dialog!: HTMLDialogElement;

  @state()
  private selected = 'none';

  private get hasAdminProduct(): boolean {
    return this.productMenuItems.some((p) => p.isAdminApp);
  }

  private _sitesData = new ContextConsumer(this, { context: sitesDataContext, subscribe: true });
  private _flags = new ContextConsumer(this, { context: flagContext, subscribe: true });

  override async firstUpdated() {
    this.dialog.addEventListener('close', () => {
      this.selected = 'none';
    });
  }

  private handleClick(event: Event) {
    if (event.target === this.dialog) {
      this.dialog.close();
    }
  }

  private subMenuListener = (e: PointerEvent | KeyboardEvent | MouseEvent | null) => {
    const isOrHasAnchor = (el: HTMLElement): boolean => {
      if (el.tagName.toLocaleLowerCase() === 'a') {
        return true;
      }
      return el.parentElement != undefined ? isOrHasAnchor(el.parentElement) : false;
    };
    if (!e || !isOrHasAnchor(e.target as HTMLElement)) {
      return;
    }
    this.dialog.close();
  };

  private closeMenu() {
    this.dialog.close();
    this.dialog.removeEventListener('click', this.subMenuListener);
  }

  private openMenu() {
    this.selected = resolveCurrentProduct(this.productMenuItems, window.location.pathname).slug;

    this.dialog.showModal();
    this.dialog.addEventListener('click', this.subMenuListener);
  }

  private navToAdmin() {
    const adminSite = this._sitesData.value?.find((site) => site.residency === 'GLOBAL');
    if (adminSite == undefined) {
      return;
    }
    this.dispatchEvent(createSelectedSiteActionEvent(adminSite.site_id));
  }

  /**
   * Reporting, Automation and PathfinderAI render in English in every locale by
   * product decision. More Products and Phantom Labs sit in the same list and
   * stay translatable — the split is deliberate, so don't wrap the English ones
   * back in msg().
   */
  override render() {
    const availableProducts = this.productMenuItems;
    const hasAdminProduct = this.hasAdminProduct;
    const flags = this._flags.value ?? {};
    const productsForMoreInfo = PRODUCT_MENU_ITEMS.filter(
      // By slug, not object identity: nothing should depend on the catalog sharing
      // references with the entitled list it is compared against.
      (product) => !availableProducts.some((entitled) => entitled.slug === product.slug) && !product.isAdminApp,
    );

    const hasAdminSite = this._sitesData.value?.find((s) => s.residency === 'GLOBAL') !== undefined;
    const showAdminLinkFlag = flags['pf-admin-menu-link'] ?? false;
    const currentProduct = resolveCurrentProduct(availableProducts, window.location.pathname);
    const productsForMoreInfoIncludeSecrets = flags['pf-show-secrets'] ?? false;
    const featureFlagFilterdProducts = productsForMoreInfo.filter(
      (p) => p.slug !== SECRETS_SLUG || (p.slug === SECRETS_SLUG && productsForMoreInfoIncludeSecrets),
    );
    const moreProductsMenu =
      !hasAdminProduct && featureFlagFilterdProducts.length > 0
        ? html`<hr />

            <spog-common-menu-product-menu
              enabled
              ?selected=${this.selected === MORE_PRODUCTS_SLUG}
              @click="${() => (this.selected = MORE_PRODUCTS_SLUG)}"
            >
              <button slot="header">
                <spog-common-menu-item-icon>
                  <btux-svg name="circle-plus"></btux-svg>
                </spog-common-menu-item-icon>
                <span>${msg('More Products')}</span>
              </button>

              <spog-common-menu-more-products-sub-menu
                slot="sub-menu"
                .products=${featureFlagFilterdProducts}
              ></spog-common-menu-more-products-sub-menu>
            </spog-common-menu-product-menu>`
        : nothing;

    const adminLink = html`
      <hr />
      <button class="admin-link" @click="${this.navToAdmin}" data-dd-action-name="admin-menu-link">
        <platform-product-icon productSlug="admin"></platform-product-icon>
        <span>${msg('Administration', { id: 'admin' })}</span>
      </button>
    `;

    let reportingMenu: TemplateResult<1> = html``;

    if (!hasAdminProduct && availableProducts.some((p) => REPORTING_PRODUCTS.includes(p.slug))) {
      reportingMenu = html` <hr />
        <spog-common-menu-product-menu
          ?enabled="${true}"
          ?selected="${this.selected == 'reporting'}"
          @click="${() => (this.selected = 'reporting')}"
          ?currentProduct="${false}"
        >
          <button slot="header">
            <btux-svg name="reporting"></btux-svg>
            <span>Reporting</span>
          </button>

          <spog-common-menu-cross-product-sub-menu slot="sub-menu">
            <section slot="items">
              <platform-reporting-menu .productMenuItems=${availableProducts}>
                <slot name="reporting-insights-menu" slot="reporting-insights-menu"></slot>
              </platform-reporting-menu>
            </section>
          </spog-common-menu-cross-product-sub-menu>
        </spog-common-menu-product-menu>`;
    }
    return html`
      <button @click="${this.openMenu}" aria-label="${msg('Menu')}" data-dd-action-name="Pathfinder Menu">
        <btux-svg name="menu"></btux-svg>
      </button>

      <dialog id="${DIALOG_ID}" aria-modal="true" @click="${this.handleClick}">
        <header>
          <a href="/" aria-label="${msg('Home')}" data-dd-action-name="Pathfinder Home">
            <btux-svg name="bt-logo"></btux-svg>
          </a>

          <button
            autofocus
            @click="${this.closeMenu}"
            aria-label="${msg('Close')}"
            data-dd-action-name="Pathfinder Close Menu"
          >
            <btux-svg name="xmark"></btux-svg>
          </button>
        </header>

        <nav>
          ${repeat(
            availableProducts,
            (product) => product.slug,
            (product) => html`
              <spog-common-menu-product-menu
                ?enabled="${true}"
                ?selected=${this.selected == product.slug}
                @click="${() => (this.selected = product.slug)}"
                ?currentProduct="${currentProduct.slug === product.slug}"
              >
                <button slot="header" data-dd-action-name="Pathfinder Menu ${product.name}">
                  <platform-product-icon productSlug=${product.slug}></platform-product-icon>
                  <span>${t(product.slug, product.name)}</span>
                </button>

                <spog-common-menu-product-sub-menu
                  slot="sub-menu"
                  product="${product.name}"
                  baseHref="${choose(
                    product.slug,
                    [
                      [REMOTE_SUPPORT_SLUG, () => urlJoin(product.baseHref, 'login') + '/'],
                      [PRIVILEGED_REMOTE_ACCESS_SLUG, () => urlJoin(product.baseHref, 'login') + '/'],
                    ],
                    () => product.baseHref,
                  ) + ''}"
                  productSlug=${product.slug}
                >
                  <slot name="${product.menuSlotName}" slot="items"></slot>
                </spog-common-menu-product-sub-menu>
              </spog-common-menu-product-menu>
            `,
          )}
          ${reportingMenu}
          ${choose(
            !hasAdminProduct && !!this._flags.value?.['platform-workflow'],
            [
              [
                true,
                () =>
                  html` <a href="/automation">
                    <btux-svg name="bt-workflows"></btux-svg>
                    Automation
                  </a>`,
              ],
              [false, () => html``],
            ],
            () => html``,
          )}
          ${choose(
            this.currentSite?.residency?.toUpperCase() === 'US',
            [
              [
                true,
                () =>
                  html` <a href="/chat">
                    <btux-svg name="ai-pathfinder"></btux-svg>
                    PathfinderAI
                    <platform-tag variant="early-access">${msg('Early Access')}</platform-tag>
                  </a>`,
              ],
              [false, () => html``],
            ],
            () => html``,
          )}
          ${choose(
            !!this._flags.value?.['menu-phantom-blog'],
            [
              [
                true,
                () =>
                  html` <a href="/phantom-labs">
                    <btux-svg name="bt-logo"></btux-svg>
                    ${msg('Phantom Labs', { id: 'menu-phantom-labs' })}
                  </a>`,
              ],
              [false, () => html``],
            ],
            () => html``,
          )}
          ${moreProductsMenu}
          ${choose(
            hasAdminSite && showAdminLinkFlag && !hasAdminProduct,
            [
              [true, () => adminLink],
              [false, () => html``],
            ],
            () => html``,
          )}
        </nav>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [MENU_TAG]: SpogMenu;
  }
}
